import type { CmmdProject, CmmdUserIdentity, CmmdWorkspace } from "@cmmd-forge/contracts";
import { readCmmdWorkIntakeSnapshot } from "../cmmd/workIntakeResource";

/** Used when CMMD has told us nothing to name the Environment after. */
export const FORGE_CLOUD_ENVIRONMENT_LABEL = "Forge Cloud";

/**
 * What one Environment carries about what to call it.
 *
 * Structural rather than a `CloudEnvironmentCatalogRow` import: the catalog
 * types already import from this directory, and naming needs exactly these
 * fields from any row shaped like one.
 */
export interface ForgeCloudEnvironmentNaming {
  /** The name someone gave this Environment, or null while it is unnamed. */
  readonly displayName: string | null;
  /** The first name of the Seat that OWNS the row, not of whoever is looking. */
  readonly userFirstName: string | null;
  /** The Organization the Environment belongs to. An unnamed one is labelled by it. */
  readonly cmmdOrganizationId: number;
  /** The CMMD user who owns the row. */
  readonly cmmdUserId: number;
}

/**
 * What the Shell knows that a row does not carry: the Organizations' names and
 * who is looking. CMMD calls an Organization a workspace on the wire.
 */
export interface ForgeCloudEnvironmentNamingContext {
  readonly organizations: ReadonlyArray<Pick<CmmdWorkspace, "id" | "name">>;
  /** The signed-in person's CMMD user id, or null before identity loads. */
  readonly viewerUserId: string | null;
}

/**
 * What to call the cloud Environment a person owns.
 *
 * Naming it after a project was only ever right while a VM belonged to one
 * project, and it made the second project's connect rename the machine the
 * first one was already on.
 *
 * Always "'s", including after a name that ends in s: "Chris's Forge Cloud"
 * beats guessing at possessive style per name.
 */
export function forgeCloudEnvironmentLabelForIdentity(
  identity: Pick<CmmdUserIdentity, "firstName"> | null,
): string {
  const firstName = identity?.firstName?.trim() ?? "";
  if (firstName.length === 0) return FORGE_CLOUD_ENVIRONMENT_LABEL;
  return `${firstName}'s ${FORGE_CLOUD_ENVIRONMENT_LABEL}`;
}

/** The slice of the work intake that names Organizations and the viewer. */
export interface ForgeCloudEnvironmentNamingIntake {
  readonly workspaces: ReadonlyArray<Pick<CmmdWorkspace, "id" | "name">>;
  readonly projects: ReadonlyArray<Pick<CmmdProject, "workspaceId" | "workspaceName">>;
  readonly identity: Pick<CmmdUserIdentity, "id"> | null;
}

/**
 * The naming context the work intake holds.
 *
 * The Organization list is the authority. A Project also carries its
 * Organization's name, which fills in for an Organization the list lacks.
 */
export function forgeCloudEnvironmentNamingContextFromIntake(
  intake: ForgeCloudEnvironmentNamingIntake,
): ForgeCloudEnvironmentNamingContext {
  const organizations = intake.workspaces.map(({ id, name }) => ({ id, name }));
  const known = new Set(organizations.map((entry) => entry.id));
  for (const project of intake.projects) {
    const id = project.workspaceId ?? null;
    const name = project.workspaceName?.trim() ?? "";
    if (id === null || name.length === 0 || known.has(id)) continue;
    known.add(id);
    organizations.push({ id, name });
  }
  return { organizations, viewerUserId: intake.identity?.id ?? null };
}

/** The name of the row's Organization, or null while the Shell has not loaded it. */
function organizationNameForRow(
  row: Pick<ForgeCloudEnvironmentNaming, "cmmdOrganizationId">,
  context: ForgeCloudEnvironmentNamingContext,
): string | null {
  const organizationId = String(row.cmmdOrganizationId);
  const name = context.organizations.find((entry) => entry.id === organizationId)?.name.trim();
  return name === undefined || name.length === 0 ? null : name;
}

/**
 * What to call one Environment row.
 *
 * An unnamed Environment is labelled by its Organization. It was labelled after
 * its owner, so a person with a Seat in two Organizations saw two identical
 * rows, and on 2026-09-17 a Force update went to the wrong one (FOR-36902).
 *
 * The owner is added only for another Seat's Environment: Connections can list
 * those, and two in one Organization would otherwise read the same. The owner
 * comes from the row, never from the viewer. While the Organization's name is
 * unknown the owner's label stands in, with the Organization's id.
 */
export function forgeCloudEnvironmentLabelForRow(
  row: ForgeCloudEnvironmentNaming,
  context: ForgeCloudEnvironmentNamingContext,
): string {
  const displayName = row.displayName?.trim() ?? "";
  if (displayName.length > 0) return displayName;
  const organizationName = organizationNameForRow(row, context);
  if (organizationName === null) {
    // The id keeps two Organizations' rows apart until the name loads. Without
    // it they would read the same again, which is the mistake this prevents.
    const ownerLabel = forgeCloudEnvironmentLabelForIdentity({ firstName: row.userFirstName });
    return `${ownerLabel} (Organization ${row.cmmdOrganizationId})`;
  }
  const ownerFirstName = row.userFirstName?.trim() ?? "";
  const isViewersOwn =
    context.viewerUserId === null || context.viewerUserId === String(row.cmmdUserId);
  if (isViewersOwn || ownerFirstName.length === 0) return organizationName;
  return `${organizationName} (${ownerFirstName})`;
}

/**
 * The label for an Environment the signed-in person is creating or connecting,
 * read from the cached CMMD work intake.
 *
 * Pre-provision only, where no row exists yet. It reads as that row will: by
 * its Organization, or after the viewer with the Organization's id while the
 * Organization's name is unknown. Anything rendering an existing row uses
 * `forgeCloudEnvironmentLabelForRow`.
 */
export function resolveForgeCloudEnvironmentLabel(cmmdOrganizationId: number): string {
  const intake = readCmmdWorkIntakeSnapshot();
  const context = forgeCloudEnvironmentNamingContextFromIntake(intake);
  const organizationName = organizationNameForRow({ cmmdOrganizationId }, context);
  if (organizationName !== null) return organizationName;
  return `${forgeCloudEnvironmentLabelForIdentity(intake.identity)} (Organization ${cmmdOrganizationId})`;
}

/**
 * The label for an existing row, read from the cached CMMD work intake.
 *
 * For code outside React, such as a toast raised from an action. A component
 * uses `useForgeCloudEnvironmentNamingContext` so the label follows the intake.
 */
export function readForgeCloudEnvironmentLabelForRow(row: ForgeCloudEnvironmentNaming): string {
  return forgeCloudEnvironmentLabelForRow(
    row,
    forgeCloudEnvironmentNamingContextFromIntake(readCmmdWorkIntakeSnapshot()),
  );
}
